import { useEffect, useState } from 'react';
import { useUserStore } from '@/stores/userStore';
import { useAuthStore } from '@/auth/authStore';
import { authApi } from '@/auth/authApi';
import { Climber } from '@/art';
import { resolveAssetUrl } from '@/shop/assetUrl';
import { audio } from '@/audio/AudioService';
import { useT } from '@/i18n/useI18n';
import {
  characterApi,
  RARITY_COLOR,
  type CharacterItem,
} from '@/characters/characterApi';

/**
 * Owned-characters section embedded in the Inventory (BAG) screen. Players
 * EQUIP climbers here; buying happens in the Shop. Only owned heroes appear.
 */
export function CharactersBagSection() {
  const t = useT();
  const profile = useUserStore((s) => s.profile);
  const setProfile = useUserStore((s) => s.setProfile);
  const token = useAuthStore((s) => s.token);
  const setUser = useAuthStore((s) => s.setUser);

  const [items, setItems] = useState<CharacterItem[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null);

  const load = () =>
    characterApi.list()
      .then((r) => {
        setItems(r.items);
        setProfile({ coins: r.coins, gems: r.gems });
      })
      .catch((e) => flash('err', String(e)));

  useEffect(() => { void load(); }, []);

  function flash(kind: 'ok' | 'err', text: string) {
    setToast({ text, kind });
    window.setTimeout(() => setToast(null), 2200);
  }

  async function equip(it: CharacterItem) {
    setBusyId(it.id);
    try {
      await characterApi.equip(it.id);
      audio.play('ui.tap_primary');
      // Optimistic: reflect the new climber on the home avatar right away.
      setProfile({ characterIdleUrl: it.idleUrl });
      await load();
      // Refresh /me so the equipped character renders in the next match and the
      // resolved idle/frame on the home avatar stays in sync with the server.
      if (token) {
        authApi.me(token)
          .then(({ user }) => {
            setUser(user);
            setProfile({ characterIdleUrl: user.characterIdleUrl, frame: user.frame });
          })
          .catch(() => {});
      }
      flash('ok', t('chars.equipped_toast', { name: it.name }));
    } catch (e: any) {
      flash('err', e?.message ?? t('chars.equip_failed'));
    }
    setBusyId(null);
  }

  // Owned heroes + any with an ACTIVE trial (temporary ownership).
  const owned = items?.filter((it) => it.owned || (it.trialUsesLeft ?? 0) > 0) ?? null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="tag" style={{ padding: '8px 2px 8px', color: 'var(--c-purple)' }}>
        {t('chars.shop_section')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {!owned ? (
          <div style={{ color: 'var(--ink-muted)', fontSize: 11, padding: 12, textAlign: 'center' }}>
            {t('chars.loading')}
          </div>
        ) : owned.length === 0 ? (
          <div style={{ color: 'var(--ink-muted)', fontSize: 11, padding: 12, textAlign: 'center' }}>
            {t('chars.bag_empty')}
          </div>
        ) : owned.map((it) => (
          <BagCharacterCard
            key={it.id}
            item={it}
            avatarColor={profile.avatarColor}
            busy={busyId === it.id}
            onEquip={() => equip(it)}
          />
        ))}
      </div>
      {toast && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: 12,
            textAlign: 'center',
            background: toast.kind === 'ok' ? 'rgba(34,211,238,0.18)' : 'rgba(230,57,70,0.18)',
            border: `1px solid ${toast.kind === 'ok' ? 'rgba(34,211,238,0.6)' : 'rgba(230,57,70,0.6)'}`,
            color: toast.kind === 'ok' ? 'var(--c-cyan)' : 'var(--c-red)',
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}

function BagCharacterCard({
  item, avatarColor, busy, onEquip,
}: {
  item: CharacterItem;
  avatarColor: 'red' | 'blue';
  busy: boolean;
  onEquip: () => void;
}) {
  const t = useT();
  const accent = RARITY_COLOR[item.rarity];
  const preview = resolveAssetUrl(item.idleUrl);
  return (
    <div
      className="card"
      style={{
        padding: 12, display: 'flex', gap: 12, alignItems: 'center',
        borderColor: item.equipped ? 'var(--c-cyan)' : `${accent}55`,
        background: item.equipped ? 'rgba(34,211,238,0.06)' : undefined,
      }}
    >
      <div style={{
        width: 60, height: 72, borderRadius: 10, background: 'rgba(0,0,0,0.3)',
        border: `1px solid ${accent}55`, display: 'flex', alignItems: 'flex-end',
        justifyContent: 'center', overflow: 'hidden', flexShrink: 0,
      }}>
        {preview ? (
          <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} draggable={false} />
        ) : (
          <div style={{ transform: 'scale(1.1)', marginBottom: -4 }}>
            <Climber color={avatarColor} pose="idle" />
          </div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="display" style={{ fontSize: 14, color: accent }}>{item.name}</span>
          <span style={{ fontSize: 8, color: accent, border: `1px solid ${accent}`, padding: '1px 5px', borderRadius: 4, textTransform: 'uppercase' }}>
            {item.rarity}
          </span>
          {!item.owned && (item.trialUsesLeft ?? 0) > 0 && (
            <span style={{ fontSize: 8, color: 'var(--c-cyan)', border: '1px solid var(--c-cyan)', padding: '1px 5px', borderRadius: 4 }}>
              📺 {t('inv.trial_left', { n: item.trialUsesLeft ?? 0 })}
            </span>
          )}
        </div>
        {item.description && (
          <div style={{ fontSize: 10, color: 'var(--ink-muted)', marginTop: 3 }}>{item.description}</div>
        )}
        <div style={{ fontSize: 9, color: 'var(--ink-dim)', marginTop: 3, fontFamily: 'var(--f-mono)' }}>
          {t('chars.win_power', { power: item.stompPower.toFixed(1) })}
        </div>

        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {item.equipped ? (
            <span className="display" style={{ fontSize: 11, color: 'var(--c-cyan)', alignSelf: 'center' }}>✓ {t('inv.equipped')}</span>
          ) : (
            <button
              disabled={busy}
              onClick={onEquip}
              className="btn btn-primary"
              style={{ fontSize: 11, padding: '7px 14px' }}
            >
              {t('inv.equip')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
